import { useState } from 'react';
import { isAxiosError } from 'axios';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, CircularProgress, Dialog, TextField, Typography } from '@mui/material';

import { instance } from '@/interfaces';
import { userInfoAtom } from '@/interfaces/member';
import { buttonBaseStyle } from '@/styles/constant';

import type { ChangeEvent } from 'react';
import type { APIResponse } from '@/@types/types';
import type { DeviceRegisterData, InventoryData } from '@/@types/device';

export const deviceAddOpenAtom = atom<boolean>(false);

const Layout = styled.div`
  padding: 3.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  > .inputWrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }
`;

export default function DeviceAdd() {
  const { t } = useTranslation();
  const [open, setOpen] = useAtom(deviceAddOpenAtom);
  const { data: userInfo } = useAtomValue(userInfoAtom);
  const [serialNumber, setSerialNumber] = useState('');
  const [verified, setVerified] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isError, setIsError] = useState(false);

  function onClose() {
    setOpen(false);
    setSerialNumber('');
    setVerified(false);
    setIsError(false);
  }
  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    setVerified(false);
    setIsError(false);
    setSerialNumber(e.target.value);
  }
  async function onVerifyClick() {
    if (verified) return;

    setIsError(false);
    setIsPending(true);

    try {
      const {
        data: { data: inventoryData },
      } = await instance.get<APIResponse<InventoryData>>(`/device/inventory/${serialNumber}`);

      // TODO: Clarify criteria for determining whether device can be registered
      if (inventoryData.inventory) {
        setVerified(true);
      } else {
        throw new Error('unknown error');
      }
    } catch (err) {
      setIsError(true);
      console.error(err);
    }

    setIsPending(false);
  }
  async function onRegisterClick() {
    setIsPending(true);

    const body: DeviceRegisterData = {
      serialNumber,
      name: serialNumber,
      deviceGroupName: 'default',
      siteId: userInfo.site[0].id,
    };
    try {
      const res = await instance.post<DeviceRegisterData>('/device', body);
      if (res.status === 201) {
        window.alert(t('Devices.message.registerOK'));
        onClose();
      } else {
        throw new Error('unknown error');
      }
    } catch (err) {
      console.error(err);
      if (isAxiosError<APIResponse>(err)) {
        let message = '';
        switch (err.response.data.errorCode) {
          case 'SERIAL_NUMBER_ALREADY_EXIST':
            message = t('Devices.message.serialNumberAlreadyExist');
            break;
          default:
            message = t('Common.message.serverErrorOccurred');
        }
        window.alert(message);
      } else {
        window.alert(t('Common.message.unknownErrorOccurred'));
      }
    }

    setIsPending(false);
  }

  return (
    <Dialog onClose={onClose} open={open}>
      <Layout>
        <div className="inputWrap">
          <TextField
            id="serialNumber"
            label={t('Devices.label.serialNumber')}
            variant="standard"
            error={isError}
            helperText={isError ? t('Devices.message.cannotRegister') : ''}
            value={serialNumber}
            onChange={onInputChange}
            sx={{
              width: 300,
            }}
          />
          <Button
            variant="outlined"
            disabled={serialNumber.length === 0}
            onClick={onVerifyClick}
            sx={{
              width: 200,
              height: 44,
              borderRadius: 22,
            }}
          >
            {!verified && isPending ? (
              <CircularProgress color="primary" size={24} />
            ) : (
              <Typography fontWeight="medium">{t('Devices.label.verify')}</Typography>
            )}
          </Button>
        </div>
        <Button
          variant="contained"
          color="info"
          disabled={!verified}
          onClick={onRegisterClick}
          sx={{
            ...buttonBaseStyle,
            width: 300,
            height: 44,
            borderRadius: 22,
          }}
        >
          {verified && isPending ? (
            <CircularProgress color="primary" size={24} />
          ) : (
            <Typography fontWeight="medium">{t('Common.label.registButton')}</Typography>
          )}
        </Button>
      </Layout>
    </Dialog>
  );
}
